import classNames from 'classnames';
import { FC, memo, useState } from 'react';

import { ReactComponent as OcrIcon } from 'assets/images/icons/ocr.svg';

import { Input, Select } from 'ui';
import Card from 'ui/Card';

import { IUserCreateSensors } from 'entities/view/userCreate';
import { SensorsApi } from 'services';
import Switch from 'ui/Switch';
import { SENSOR_TYPES } from 'utils/constants';
import { filterKeyInput } from 'utils/helpers/appkey';

type SensorTypeOption = { value: string; label: string };

type PropTypes = {
  setError: (field: string, value: string | undefined) => void;
  errorFieldPrefix: string;
  title?: string;
  id?: string;
  sensor: IUserCreateSensors;
  detailIndex: number;
  sensorIndex: number;
  sensorTypes?: SensorTypeOption[];
  onRemoveSensor?: (detailIndex: number, sensorIndex: number) => void;
  onSensorChange: (
    detailIndex: number,
    sensorIndex: number,
    field: string,
    value: string | boolean,
  ) => void;
  sensorTypeError?: string;
  sensorIdError?: string;
  sensorKeyError?: string;
};

const SensorDetails: FC<PropTypes> = ({
  id,
  sensor,
  setError,
  errorFieldPrefix,
  title = 'Sensor',
  detailIndex,
  sensorIndex,
  sensorTypes = SENSOR_TYPES,
  onRemoveSensor,
  onSensorChange,
  sensorTypeError,
  sensorIdError,
  sensorKeyError,
}) => {
  const [isEUILoading, setEUILoading] = useState(false);
  const [isKeyLoading, setKeyLoading] = useState(false);

  const validateSensorId = (value: string) => {
    const regex = /^[a-zA-Z0-9_-]*$/;
    return regex.test(value) || value === '';
  };

  const handleSensorIdChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (validateSensorId(value)) {
      onSensorChange(detailIndex, sensorIndex, 'sensorEui', value);

      if (value.length >= 16) {
        try {
          const response = await SensorsApi.getDeviceInfo(value);
          const deviceName = response?.data?.device_info?.data?.[0]?.device_name;

          if (deviceName) {
            const matchedType = (sensorTypes || [])?.find((type) => {
              const modelMatch = deviceName.match(/S\d{3,4}/i);
              return modelMatch && type.label.toLowerCase().includes(modelMatch[0].toLowerCase());
            });

            if (matchedType) {
              onSensorChange(detailIndex, sensorIndex, 'type', matchedType.value);
            }
          }
        } catch (error) {
          console.error('Failed to auto-detect sensor type:', error);
        }
      }
    }
  };

  const handleActiveChange = (checked: boolean) => {
    onSensorChange(detailIndex, sensorIndex, 'active', checked);
  };

  const handleLoadFiles = async (file: File) => {
    try {
      setEUILoading(true);

      const data = await SensorsApi.recognizeSensor(file);

      if (data?.data?.recognized) {
        onSensorChange(detailIndex, sensorIndex, 'sensorEui', data?.data?.sensor_id);
      } else {
        if (data?.data?.message) {
          setError(`${errorFieldPrefix}.sensorEui`, data?.data?.message);
        }
      }
    } catch {
    } finally {
      setEUILoading(false);
    }
  };

  const handleRightIconClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = false;

    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;

      if (!!files && !!files.length) {
        const file = files?.[0];
        const maxSize = 5 * 1024 * 1024;
        const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(
          file.type,
        );
        const field = `${errorFieldPrefix}.sensorEui`;

        if (file.size > maxSize) {
          setError(field, 'File is more than 5mb');
        } else if (!isValidType) {
          setError(field, 'File should be JPEG/PNG/WEBP formats');
        } else {
          handleLoadFiles(files?.[0]);
        }
      }
    };

    document.body.appendChild(input);
    input.click();
    input.remove();
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = filterKeyInput(e.target.value);
    onSensorChange(detailIndex, sensorIndex, 'sensorKey', filtered);
    if (sensorKeyError) {
      setError(`${errorFieldPrefix}.sensorKey`, undefined);
    }
  };

  const handleKeyRecognize = async (file: File) => {
    try {
      setKeyLoading(true);
      const data = await SensorsApi.recognizeSensorAppKey(file);

      if (data?.data?.recognized && data?.data?.appkey) {
        onSensorChange(detailIndex, sensorIndex, 'sensorKey', data.data.appkey);
        setError(`${errorFieldPrefix}.sensorKey`, undefined);
      } else {
        setError(
          `${errorFieldPrefix}.sensorKey`,
          data?.data?.message || 'Unable to recognize the Key. Please try again.',
        );
      }
    } catch {
      setError(
        `${errorFieldPrefix}.sensorKey`,
        'Unable to recognize the Key. Please try again.',
      );
    } finally {
      setKeyLoading(false);
    }
  };

  const handleKeyUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.multiple = false;

    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;

      if (!!files && !!files.length) {
        const file = files[0];
        const maxSize = 5 * 1024 * 1024;
        const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(
          file.type,
        );
        const field = `${errorFieldPrefix}.sensorKey`;

        if (file.size > maxSize) {
          setError(field, 'File is more than 5mb');
        } else if (!isValidType) {
          setError(field, 'File should be JPEG/PNG/WEBP formats');
        } else {
          handleKeyRecognize(file);
        }
      }
    };

    document.body.appendChild(input);
    input.click();
    input.remove();
  };

  return (
    <Card
      title={
        <div className="step-two__sensor__headline">
          <h2>{title}</h2>
          {!!id && !sensor?.isNew && (
            <Switch
              id={`sensor-active-${detailIndex}-${sensorIndex}`}
              name={`sensor-active-${detailIndex}-${sensorIndex}`}
              checked={sensor?.active ?? true}
              onChange={handleActiveChange}
            />
          )}
        </div>
      }
      className={classNames('step-two__sensor', 'has-parent')}
      onRemove={!!onRemoveSensor ? () => onRemoveSensor(detailIndex, sensorIndex) : undefined}
    >
      <div className="step-two__sensor-inputs">
        <Select
          withSearch
          label="Sensor type"
          value={sensor?.type}
          placeholder="Type"
          name={`sensor-type-${detailIndex}-${sensorIndex}`}
          options={sensorTypes}
          className="step-two__sensor-input"
          onChange={(value: string) => onSensorChange(detailIndex, sensorIndex, 'type', value)}
          error={sensorTypeError}
          disabled={!!sensor?.id}
        />

        <Input
          label="Sensor ID/EUI"
          value={sensor?.sensorEui}
          placeholder="ID/EUI"
          name={`sensor-id-${detailIndex}-${sensorIndex}`}
          wrapperClassName="step-two__sensor-input"
          rightIcon={OcrIcon}
          iconSize={20}
          onRightIconClick={handleRightIconClick}
          onChange={handleSensorIdChange}
          maxLength={100}
          variant="white"
          error={sensorIdError}
          disabled={!!sensor?.id || isEUILoading}
        />

        <Input
          label="Key"
          value={sensor?.sensorKey ?? ''}
          placeholder="Key"
          name={`sensor-key-${detailIndex}-${sensorIndex}`}
          wrapperClassName="step-two__sensor-input"
          rightIcon={OcrIcon}
          iconSize={20}
          rightIconTooltip="Upload a clear, unblurred photo with the full Key readable — no pixelation, cropping, or text covering the key."
          onRightIconClick={handleKeyUploadClick}
          onChange={handleKeyChange}
          variant="white"
          error={sensorKeyError}
          disabled={!!sensor?.id || isKeyLoading}
        />
      </div>
    </Card>
  );
};

export default memo(SensorDetails);
